import type { HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  CreateRoleParams,
  DeleteRoleParams,
  ListRolesParams,
  Role,
  RolePermission,
  RoleSort,
  RolesPage,
  RoleSortField,
  UpdateRoleParams,
} from "./roles.types";

export const ROLE_PATHS = {
  LIST: "/api/plataforma/perfis",
} as const;

export function buildRolePath(id: string): string {
  return `${ROLE_PATHS.LIST}/${encodeURIComponent(id)}`;
}

export const DEFAULT_ROLE_SORT: RoleSort = { field: "nome", order: "asc" };

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readFirstText(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const text = readText(raw[key]);
    if (text) return text;
  }

  return "";
}

function readCount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return null;
}

function readFirstCount(raw: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const count = readCount(raw[key]);
    if (count !== null) return count;
  }

  return null;
}

function extractItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return [];

  for (const key of ["data", "items", "content", "perfis"]) {
    const nested = payload[key];
    if (nested !== undefined) return extractItems(nested);
  }

  return [];
}

function toRolePermission(raw: unknown): RolePermission | null {
  // O serviço pode devolver apenas o nome da permissão em vez do objeto completo.
  if (typeof raw === "string") {
    return raw.trim() ? { id: raw, codigo: raw, nome: raw } : null;
  }

  if (!isRecord(raw)) return null;

  const id = readText(raw.id);
  const codigo = readFirstText(raw, "code", "codigo");
  const nome = readFirstText(raw, "name", "nome");
  if (!id && !codigo && !nome) return null;

  return { id: id || codigo || nome, codigo: codigo || nome, nome: nome || codigo };
}

function extractRolePermissions(raw: Record<string, unknown>): RolePermission[] {
  for (const key of ["permissoes", "permissions", "permissoesAssociadas"]) {
    const value = raw[key];
    if (value === undefined) continue;

    return extractItems(value)
      .map(toRolePermission)
      .filter((permission): permission is RolePermission => permission !== null);
  }

  return [];
}

/** Desembrulha o envelope do BFF (`{ success, data }`) até chegar ao recurso. */
function extractResource(payload: unknown): unknown {
  if (!isRecord(payload)) return payload;

  for (const key of ["data", "perfil"]) {
    const nested = payload[key];
    if (isRecord(nested)) return extractResource(nested);
  }

  return payload;
}

function toRole(raw: unknown): Role | null {
  if (!isRecord(raw)) return null;

  const id = readText(raw.id);
  const codigo = readFirstText(raw, "codigo", "code");
  if (!id && !codigo) return null;

  return {
    id: id || codigo,
    codigo,
    nome: readFirstText(raw, "nome", "name"),
    permissoes: extractRolePermissions(raw),
    // Contrato do BFF (`GET /api/plataforma/perfis`): só a contagem, nunca a lista de IDs.
    quantidadeUsuarios: readFirstCount(raw, "quantidadeUsuarios") ?? 0,
  };
}

function extractTotal(payload: unknown): number | null {
  if (!isRecord(payload)) return null;

  const meta = isRecord(payload.meta) ? payload.meta : undefined;
  const pagination = isRecord(meta?.pagination)
    ? meta.pagination
    : isRecord(payload.pagination)
      ? payload.pagination
      : undefined;

  for (const source of [pagination, meta, payload]) {
    if (!source) continue;

    const count = readFirstCount(source, "total", "totalRecords", "totalElements", "totalItens");
    if (count !== null) return count;
  }

  return isRecord(payload.data) ? extractTotal(payload.data) : null;
}

function compareBy(field: RoleSortField, a: Role, b: Role): number {
  if (field === "quantidadeUsuarios") return a.quantidadeUsuarios - b.quantidadeUsuarios;

  return a[field].localeCompare(b[field], "pt-BR", { sensitivity: "base" });
}

function sortRoles(roles: Role[], field: RoleSortField, order: "asc" | "desc"): Role[] {
  const direction = order === "desc" ? -1 : 1;

  return [...roles].sort((a, b) => compareBy(field, a, b) * direction);
}

export async function listRoles({
  pesquisa,
  permissaoIds,
  pagina,
  tamanhoPagina,
  ordenarPor = DEFAULT_ROLE_SORT.field,
  ordem = DEFAULT_ROLE_SORT.order,
  signal,
}: ListRolesParams = {}): Promise<RolesPage> {
  const term = pesquisa?.trim();
  const permissoes = (permissaoIds ?? []).filter((id) => id.trim() !== "");

  const params: Record<string, string | number> = {};
  if (term) params.pesquisa = term;
  // Contrato da história pai: `permissaoId`, com múltiplos valores separados por vírgula.
  if (permissoes.length > 0) params.permissaoId = permissoes.join(",");
  if (pagina) params.pagina = pagina;
  if (tamanhoPagina) params.tamanhoPagina = tamanhoPagina;
  params.ordenarPor = ordenarPor;
  params.ordem = ordem;

  const config: HttpRequestConfig = { signal, params };

  const response = await getHttpClient().get<unknown>(ROLE_PATHS.LIST, config);

  const roles = sortRoles(
    extractItems(response.data)
      .map(toRole)
      .filter((role): role is Role => role !== null),
    ordenarPor,
    ordem,
  );

  return { roles, total: extractTotal(response.data) ?? roles.length };
}

/** Corpo de gravação do contrato da história pai: `nome` e `permissaoIds[]`. */
function toRoleBody(nome: string, permissaoIds: string[]) {
  return {
    nome: nome.trim(),
    permissaoIds: permissaoIds.filter((id) => id.trim() !== ""),
  };
}

/**
 * Cadastra um perfil com o conjunto de permissões marcado. O código é gerado
 * pelo serviço e só volta a aparecer para o usuário na recarga da listagem.
 *
 * Retorna o perfil devolvido pela API ou `null` quando a resposta não traz
 * corpo utilizável (ex.: 201/204 sem conteúdo).
 */
export async function createRole({
  nome,
  permissaoIds,
  signal,
}: CreateRoleParams): Promise<Role | null> {
  const response = await getHttpClient().post<unknown>(
    ROLE_PATHS.LIST,
    toRoleBody(nome, permissaoIds),
    { signal },
  );

  return toRole(extractResource(response.data));
}

/**
 * Atualiza o nome e as permissões de um perfil. RN02: o serviço sincroniza a
 * associação, então `permissaoIds` precisa carregar o conjunto completo.
 */
export async function updateRole({
  id,
  nome,
  permissaoIds,
  signal,
}: UpdateRoleParams): Promise<Role | null> {
  const response = await getHttpClient().put<unknown>(
    buildRolePath(id),
    toRoleBody(nome, permissaoIds),
    { signal },
  );

  return toRole(extractResource(response.data));
}
/**
 * Exclui o perfil selecionado. A exclusão é lógica no serviço (RN02), mas para a
 * tela é definitiva: o registro deixa de aparecer na listagem.
 *
 * O serviço rejeita a operação quando o perfil ainda tem usuários vinculados
 * (RN03); o erro chega ao chamador para que a tela apresente o bloqueio.
 */
export async function deleteRole({ id, signal }: DeleteRoleParams): Promise<void> {
  await getHttpClient().delete<unknown>(buildRolePath(id), { signal });
}
